import { UiOption } from '../../helpers';
import { McpBlockAction } from '../policy-model-v1/mcp-controls';
import { McpToolClassificationAction } from '../mcp-tool';
import { ConnectorModel } from '../connector/connector.model';

export type UserEventRole =
  | 'context'
  | 'user'
  | 'assistant'
  | 'system'
  | 'tool_use'
  | 'tool_response';

type SdDetection = { startPos: number; endPos: number; score: number };

export type SdPosition = SdDetection & { type: string };

export type RegexSdDetection = SdDetection & { classifierId: string; name: string };

export type SensitiveData = {
  detected: boolean;
  detections: SdPosition[][];
  regexDetected?: boolean;
  regexDetections?: RegexSdDetection[][];
};

export type IntentType = 'custom' | 'system';

export type IntentDetails = {
  name: string;
  // if type is custom, id should be defined and confidence should be defined
  type: IntentType;
  id?: string;
  confidence?: number;
};

export type LegacyIntentScore = {
  name: string;
  confidence: number;
  actionName: string;
  departmentName: string;
  summary: string;
};

export type IntentScore = {
  name: string;
  type: 'system' | 'custom';
  id?: string;
  confidence?: number;
};

export type UserEventIntent = {
  requestViolation: boolean;
  responseViolation: boolean;
  request: LegacyIntentScore[];
  response: LegacyIntentScore[];
};

export type IntentScoreKey = 'domains' | 'actions' | 'topics';

export type UserEventIntents = {
  input: Record<IntentScoreKey, IntentScore[]>;
  output: Record<IntentScoreKey, IntentScore[]>;
};

export type UserClaim = {
  userId: string;
  email: string;
  name: string;
  role: string;
  orgId: string;
  groups: string[];
  spUserId: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isUserClaim = (obj: any): obj is UserClaim => {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    typeof (obj as Record<string, unknown>).spUserId === 'string'
  );
};

export type ClientAddr = {
  remoteIp: string;
  xForwardedFor: string;
  xRealIp: string;
  internalIp: string;
};

export type ClientIdentifiers = {
  userAgent: string;
  ingressOrgConnector?: IngressConnector;
};

// Raw value stored by the gateway
export type IngressConnector = {
  id?: string;
  name?: string;
  type?: string;
};

// Raw value stored by the gateway
export type InferenceConnector = {
  connectorId?: string;
  connectorName?: string;
  connectorType?: string;
};

export type UserEventConnectors = {
  // The network-edge integration the traffic arrived through
  ingress?: ConnectorModel;
  // The AI-vendor connector the request was routed to
  inference?: ConnectorModel;
};

export enum Violation {
  SENSITIVE_DATA = 'Sensitive Data',
  PII = 'PII',
  INTENT = 'Intent',
  ACCESS = 'Access',
  CONFIDENTIAL_DATA = 'Confidential Data',
  PROMPT_INJECTION = 'Prompt Injection',
  TOXICITY = 'Toxicity',
  CODE = 'Code',
  BIAS = 'Bias',
  RISK = 'Risk',
  INPUT = 'Input', // SP-1570: deprecated
  OUTPUT = 'Output', // SP-1570: deprecated
  TOOL = 'Tool',
  TRAINING = 'Training',
  LICENSE = 'License',
  FILE_UPLOAD = 'File Upload',
  ENTITY = 'Entity',
}

export type Violations = {
  sensitiveData: boolean;
  pii: boolean;
  intent: boolean;
  access: boolean;
  confidentialData: boolean;
  promptInjection: boolean;
  toxicity: boolean;
  code: boolean;
  bias: boolean;
  risk: boolean;
  input: boolean; // SP-1570: deprecated
  output: boolean; // SP-1570: deprecated
  tool: boolean;
  training: boolean;
  license: boolean;
  fileUpload: boolean;
  regex: boolean;
};

export type PolicyDecision = {
  decision: UserEventAction;
  label: string;
  code: number;
  codeLabel: string;
  violations: Violations;
  isGroupPolicy: boolean;
  groupId: string;
};

// we should never see 0,3,6
export enum UserEventAction {
  CONTINUE = 0,
  ALLOW = 1,
  BLOCK = 2,
  DROP = 3,
  PORTAL = 4,
  REROUTE = 5,
  PASSTHROUGH = 6,
  ERROR = 7,
  DOWNTIME = 8,
  HTTPERROR = 9,
  AUTH = 10,
  INTERCEPTAUTH = 11,
  INLINEBLOCK = 12,
  REDACT = 13,
}

type ShortcutInputType = 'text' | 'textarea' | 'range' | 'switch' | 'select' | 'select-multi';

type ShortcutValue = string | string[];

type ShortcutStep = {
  name: string;
  title: string;
  description: string;
  inputType: ShortcutInputType;
  options?: UiOption[];
};

export type ShortcutMessage = {
  id: string;
  name: string;
  instructions: string;
  steps: ShortcutStep[];
  values: ShortcutValue[];
};

export type ContextDetails = {
  name: string;
  origin: 'user' | 'enterprise';
  source: string;
  purpose?: string;
  summary?: string;
  type: string; // @todo file|context-datasource?
};

export type UserEventMessage = {
  messageId?: string;
  requestId: string;
  role: UserEventRole;
  content: string;
  origContent?: string;
  timestamp: number;
  shortcut: ShortcutMessage;
  contextDetails: ContextDetails;
  violations: Violations;
  sensitiveDataDetections: SdPosition[];
  sensitiveDataRegexDetections: RegexSdDetection[];
  additionalProperties?: AdditionalProperties;
};

export interface AdditionalProperties {
  id: string;
  input?: Record<string, unknown>;
  name?: string;
}

export interface ToolResultProperties extends AdditionalProperties {
  toolUseId: string;
  agentId?: string;
  totalTokens?: number;
  toolUses?: number;
  durationMs?: number;
}

export type FirewallDecision = {
  action: string;
  target: { name: string };
};

export type PromptClassification = {
  request: RiskAssessment;
};

export type RiskLevel = 'low' | 'medium' | 'high' | 'unknown';

type DataSensitivityLevel = 'CRITICAL' | 'PUBLIC' | 'INTERNAL' | 'CONFIDENTIAL' | 'UNKNOWN';

export type EvaluatedLevel = RiskLevel | DataSensitivityLevel;

export type RiskAssessment = {
  overallRiskLevel: RiskLevel;
  overallRiskLevelSortValue: number;
  input?: {
    overallRiskLevel: RiskLevel;
    overallRiskLevelSortValue: number;
    contentRisk: RiskLevel;
    intentRisk: RiskLevel;
    dataSensitivity: DataSensitivityLevel;
    dataExposureImpact: RiskLevel;
    harmfulContentRisk: RiskLevel;
    promptInjectionRisk: RiskLevel;
    serviceRisk: RiskLevel;
  };
  output?: {
    overallRiskLevel: RiskLevel;
    overallRiskLevelSortValue: number;
    contentRisk: RiskLevel;
    harmfulContentRisk: RiskLevel;
    biasRisk: RiskLevel;
    serviceRisk: RiskLevel;
  };
};

export type RequestMetadata = Record<
  string,
  { value: string | string[]; type: string; adminVisible: boolean }
>;

export type FileUpload = {
  fileName: string;
  fileSize: number;
  violation: boolean;
};

export type AssistantData = {
  id: string;
  name?: string;
};

export type McpTool = {
  id: string;
  name: string;
  description: string;
  serverId: string;
  serverName: string;
  classification: {
    action: McpToolClassificationAction;
  };
  blocked: boolean;
  blockAction: McpBlockAction;
  execution: {
    detected: boolean;
    detectable: boolean;
  };
};
